import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error('Something went wrong');
      setSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center px-4">
      <Card data-testid="forgot-password-card" className="w-full max-w-md bg-white border border-slate-100 shadow-lg rounded-xl p-8">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Errands Boy" className="h-20 w-20 object-contain mx-auto mb-4" />
          {sent ? (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Check Your Email</h1>
              <p className="text-slate-600">
                If an account exists with <span className="font-medium">{email}</span>, we've sent a password reset link. It expires in 1 hour.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Forgot Password</h1>
              <p className="text-slate-600">Enter your email and we'll send you a reset link.</p>
            </>
          )}
        </div>

        {!sent && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 rounded-lg"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="send-reset-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-black text-white rounded-full py-3 font-medium shadow-lg btn-hover"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        )}

        <button
          data-testid="back-to-login-btn"
          onClick={() => navigate('/login')}
          className="w-full mt-6 text-sm text-slate-600 hover:text-slate-900 flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
      </Card>
    </div>
  );
}
